// TelcoTrack - ChartRangeControls (P1 #10)
// Compact calendar-icon popover: granularity (M/W/D) + date-range for KPI charts.

import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { openNativeDatePicker } from '../../../utils/manager-dashboard-range.util';
import {
  claimFilterOverlay,
  releaseFilterOverlay,
} from '../../../utils/filter-overlay-coordinator.util';
import { filterOverlayPanelStyle } from '../../../utils/filter-overlay-position.util';

export interface ChartGranularityOption {
  id: string;
  label: string;
  hint?: string;
}

@Component({
  selector: 'app-chart-range-controls',
  templateUrl: './chart-range-controls.component.html',
  styleUrls: ['./chart-range-controls.component.scss'],
})
export class ChartRangeControlsComponent implements OnDestroy {
  @Input() granularityOptions: ChartGranularityOption[] = [];
  @Input() granularity = '';
  @Input() granularityAriaLabel = 'Rozlišení času';
  @Input() disabled = false;

  @Input() rangeStart = '';
  @Input() rangeEnd = '';
  @Input() rangeStartLabel = 'Od';
  @Input() rangeEndLabel = 'Do';
  @Input() rangeInputType: 'date' | 'week' | 'month' = 'date';
  @Input() rangeErrorMessage: string | null = null;
  /** Unique prefix so multiple instances on one page don't collide on native `id`. */
  @Input() idPrefix = 'chart-range';

  @Output() granularityChange = new EventEmitter<string>();
  @Output() rangeStartChange = new EventEmitter<string>();
  @Output() rangeEndChange = new EventEmitter<string>();
  /** Fired on blur of either range field — parent typically re-fetches/validates here. */
  @Output() rangeBlur = new EventEmitter<void>();

  @ViewChild('triggerBtn') private triggerBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelEl') private panelEl?: ElementRef<HTMLElement>;

  panelOpen = false;
  panelStyle: { [key: string]: string } = {};

  private panelInBody = false;
  private panelOriginalParent: Node | null = null;
  private readonly reposition = (): void => this.updatePanelPosition();

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get panelAriaLabel(): string {
    return 'Období a rozlišení času';
  }

  get triggerTitle(): string {
    const gran = this.granularityOptions.find((o) => o.id === this.granularity)?.label;
    const range = [this.rangeStart, this.rangeEnd].filter(Boolean).join(' – ');
    return [gran, range].filter(Boolean).join(' · ') || this.panelAriaLabel;
  }

  trackOption(_index: number, option: ChartGranularityOption): string {
    return option.id;
  }

  selectGranularity(id: string): void {
    if (this.disabled || id === this.granularity) return;
    this.granularityChange.emit(id);
  }

  onRangeStartInput(value: string): void {
    this.rangeStartChange.emit(value);
  }

  onRangeEndInput(value: string): void {
    this.rangeEndChange.emit(value);
  }

  onBlur(): void {
    this.rangeBlur.emit();
  }

  openPicker(input: HTMLInputElement, event: Event): void {
    openNativeDatePicker(input, event);
  }

  togglePanel(): void {
    if (this.disabled) return;
    if (this.panelOpen) {
      this.closePanel();
      return;
    }
    claimFilterOverlay(this);
    this.panelOpen = true;
    this.updatePanelPosition();
    requestAnimationFrame(() => {
      this.movePanelToBody();
      this.updatePanelPosition();
    });
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  closePanel(): void {
    if (!this.panelOpen) return;
    this.restorePanelToHost();
    this.panelOpen = false;
    releaseFilterOverlay(this);
    this.teardownReposition();
  }

  onPanelPointerDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelOpen) return;
    const target = event.target as Node;
    const insideHost = this.host.nativeElement.contains(target);
    const insidePanel = this.panelEl?.nativeElement.contains(target) ?? false;
    if (!insideHost && !insidePanel) {
      this.closePanel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closePanel();
  }

  ngOnDestroy(): void {
    this.teardownReposition();
    releaseFilterOverlay(this);
    if (this.panelInBody) {
      this.panelEl?.nativeElement.remove();
      this.panelInBody = false;
      this.panelOriginalParent = null;
    }
  }

  private movePanelToBody(): void {
    const panel = this.panelEl?.nativeElement;
    if (!panel || this.panelInBody) return;
    this.panelOriginalParent = panel.parentNode;
    document.body.appendChild(panel);
    this.panelInBody = true;
  }

  private restorePanelToHost(): void {
    if (!this.panelInBody) return;
    const panel = this.panelEl?.nativeElement;
    if (panel && this.panelOriginalParent) {
      this.panelOriginalParent.appendChild(panel);
    }
    this.panelInBody = false;
    this.panelOriginalParent = null;
  }

  private updatePanelPosition(): void {
    const btn = this.triggerBtn?.nativeElement;
    if (!btn) return;
    this.panelStyle = filterOverlayPanelStyle(btn, this.panelEl?.nativeElement, 288);
  }

  private teardownReposition(): void {
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }
}
